import type { AuthOrganization, TokenPair } from '../api/types';
import { refreshPreferences } from '../../shared/preferences/store';

/**
 * Browser token storage for the rotating-refresh model.
 *
 * Security posture (see 05-operations-security-and-scaling.md and the open
 * BFF-vs-browser-token decision):
 *  - The short-lived access token is kept in memory only. It is never written
 *    to storage and never placed in URLs.
 *  - The one-time refresh token and its binding context (organizationId +
 *    productCode, required by /auth/refresh) live in sessionStorage so a reload
 *    in the same tab can restore the session. sessionStorage is per-tab and is
 *    cleared when the tab closes; localStorage is never used for tokens.
 *  - The user's organization choices (id/code/name only — no credentials) are
 *    also cached in sessionStorage to power the organization switcher after a
 *    session restore.
 *  - Every key is cleared on logout/session-expiry (WP-706: organization
 *    switch clears tenant-keyed state and obtains a new token).
 *
 * Keys are namespaced per app so this module can later become the shared
 * WP-700 token adapter.
 */

const PREFIX = 'architech.platform.v1.';

interface PersistedSession {
  refreshToken: string;
  organizationId: string;
  productCode: string;
}

const SESSION_KEY = `${PREFIX}session`;
const ORG_CHOICES_KEY = `${PREFIX}orgChoices`;

const memory = {
  accessToken: null as string | null,
};

export interface RefreshContext {
  refreshToken: string;
  organizationId: string;
  productCode: string;
}

export const tokenStorage = {
  getAccessToken(): string | null {
    return memory.accessToken;
  },

  getRefreshContext(): RefreshContext | null {
    try {
      const raw = window.sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PersistedSession;
      if (!parsed.refreshToken || !parsed.organizationId) return null;
      return {
        refreshToken: parsed.refreshToken,
        organizationId: parsed.organizationId,
        productCode: parsed.productCode,
      };
    } catch {
      return null;
    }
  },

  applyTokenPair(pair: TokenPair, context: Omit<RefreshContext, 'refreshToken'>): void {
    memory.accessToken = pair.accessToken;
    void refreshPreferences();
    const session: PersistedSession = {
      refreshToken: pair.refreshToken,
      organizationId: context.organizationId,
      productCode: context.productCode,
    };
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  },

  getOrgChoices(): AuthOrganization[] {
    try {
      const raw = window.sessionStorage.getItem(ORG_CHOICES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as AuthOrganization[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  setOrgChoices(choices: AuthOrganization[]): void {
    window.sessionStorage.setItem(ORG_CHOICES_KEY, JSON.stringify(choices));
  },

  clear(): void {
    memory.accessToken = null;
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(ORG_CHOICES_KEY);
  },
};
